<template>
  <q-tab-pane name="general" class="no-padding">
    <q-scroll-area
      class="full-width customer-card__tab-scroll"
      :thumb-style="{ width: '5px','background-color': '#555',opacity: 1 }"
    >
      <div class="text-select">
        <q-list no-border>
          <!-- Customer number -->
          <template v-if="customer.customer_number">
            <q-item class="customer-card__customer-number">
              <q-item-side icon="account_circle" color="faded" class="customer-card__icon"/>
              <q-item-main>
                <q-item-tile label class="customer-card__customer-number-entry">
                  {{ formattedBusinessPartnerType }}
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__payment-deadline-label">
                  {{ $t('business_partner_type') }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item class="customer-card__customer-number">
              <q-item-main inset>
                <q-item-tile label class="customer-card__customer-number-entry">
                  {{ customer.customer_number }}
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__payment-deadline-label">
                  {{ $t('customer_number') }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item v-if="customer.accounts_receivable_number" class="customer-card__customer-number">
              <q-item-main inset>
                <q-item-tile label class="customer-card__customer-number-entry">
                  {{ customer.accounts_receivable_number }}
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__payment-deadline-label">
                  {{ $t('accounts_receivable_number') }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item v-if="customer.accounts_payable_number" class="customer-card__customer-number">
              <q-item-main inset>
                <q-item-tile label class="customer-card__customer-number-entry">
                  {{ customer.accounts_payable_number }}
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__payment-deadline-label">
                  {{ $t('accounts_payable_number') }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- Company -->
          <template v-if="personCompany">
            <q-item class="customer-card__company">
              <q-item-side icon="business" color="faded" class="customer-card__icon"/>
              <q-item-main>
                <q-item-tile label class="customer-card__company-entry">
                  <router-link
                    :to="{ name: 'customer', params: { teamId: activeTeam.id, customerId: personCompany.id } }"
                    class="router-link"
                  >
                    {{ personCompany.name }}
                  </router-link>
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- VAT Number -->
          <template v-if="customer.vat_number">
            <q-item class="customer-card__vat-number">
              <q-item-side icon="account_balance" color="faded" class="customer-card__icon"/>
              <q-item-main>
                <q-item-tile label class="customer-card__vat-number-entry">
                  {{ customer.vat_number }}
                </q-item-tile>
                <q-item-tile sublabel>
                  {{ $t('vat_number') }}
                </q-item-tile>
              </q-item-main>
            </q-item>
          </template>

          <!-- Tax Number -->
          <template v-if="customer.tax_number">
            <q-item class="customer-card__vat-number">
              <q-item-main inset>
                <q-item-tile label class="customer-card__tax-number-entry">
                  {{ customer.tax_number }}
                </q-item-tile>
                <q-item-tile sublabel>
                  {{ $t('tax_number') }}
                </q-item-tile>
              </q-item-main>
            </q-item>
          </template>

          <q-item-separator v-if="customer.vat_number || customer.tax_number"/>

          <!-- Website -->
          <template v-if="customer.website">
            <q-item class="customer-card__website">
              <q-item-side icon="public" color="faded" class="customer-card__icon"/>
              <q-item-main>
                <q-item-tile label class="customer-card__website-entry">
                  <a :href="websiteUrl" target="_blank" rel="noopener noreferrer">{{ website }}</a>
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- Default Payment Deadline -->
          <template v-if="customer.default_payment_deadline">
            <q-item class="customer-card__payment-deadline">
              <q-item-side icon="calendar_today" color="faded" class="customer-card__icon"/>
              <q-item-main>
                <q-item-tile label class="customer-card__payment-deadline-entry">
                  {{ customer.default_payment_deadline }}
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__payment-deadline-label">
                  {{ $tc('day', 2) }} {{ $t('payment_deadline') }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- Phone numbers -->
          <template v-if="customer.phone_numbers && customer.phone_numbers.data.length">
            <q-item v-for="(phoneNumber, index) in customer.phone_numbers.data"
                    :key="'p_' + phoneNumber.id"
                    class="customer-card__phone"
            >
              <q-item-side v-if="index === 0" icon="phone" color="faded" class="customer-card__icon"/>
              <q-item-main :inset="index > 0">
                <q-item-tile label class="customer-card__phone-entry">
                  {{ phoneNumber.number }}
                  <q-icon name="stars" color="faded" v-if="phoneNumber.id === customer.default_phone_number_id"/>
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__phone-label">
                  {{ phoneNumber.label }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- E-Mail addresses -->
          <template v-if="customer.email_addresses && customer.email_addresses.data.length">
            <q-item v-for="(emailAddress, index) in customer.email_addresses.data"
                    :key="'e_' + emailAddress.id"
                    class="customer-card__email"
            >
              <q-item-side v-if="index === 0" icon="email" color="faded" class="customer-card__icon"/>
              <q-item-main :inset="index > 0">
                <q-item-tile label class="customer-card__email-entry">
                  <a :href="'mailto:' + emailAddress.email">{{ emailAddress.email }}</a>
                  <q-icon name="stars" color="faded" v-if="emailAddress.id === customer.default_email_address_id"/>
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__email-label">{{ emailAddress.label }}</q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- Addresses -->
          <template v-if="customer.addresses  && customer.addresses.data.length">
            <q-item v-for="(address, index) in customer.addresses.data"
                    :key="'a_' + address.id"
                    class="customer-card__address"
            >
              <q-item-side v-if="index === 0" icon="pin_drop" color="faded" class="customer-card__icon"/>
              <q-item-main :inset="index > 0">
                <q-item-tile label class="customer-card__address-entry">
                  <div class="row">
                    <div v-html="address.formatted_address"></div>
                    <q-icon name="stars" color="faded" class="q-pl-md"
                            v-if="address.id === customer.default_address_id"/>
                  </div>
                </q-item-tile>
                <q-item-tile sublabel class="customer-card__address-label">{{ address.label }}</q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>

          <!-- Bank details -->
          <template v-if="customer.bank_details  && customer.bank_details.data.length">
            <q-item v-for="(bankDetails, index) in customer.bank_details.data"
              :key="'b_' + bankDetails.id"
              class="customer-card__bank-details"
            >
              <q-item-side
                v-if="index === 0"
                icon="account_balance"
                color="faded"
                class="customer-card__icon"
              />
              <q-item-main :inset="index > 0">
                <q-item-tile label class="customer-card__bank-details-entry">
                  <div class="row">
                    <div>
                      {{ bankDetails.account_number }}<br>
                      {{ bankDetails.bank_identifier }}&nbsp;{{ bankDetails.bank_name }}<br>
                      {{ bankDetails.account_owner }}
                    </div>
                    <q-icon
                      v-if="bankDetails.id === customer.default_bank_details_id"
                      name="stars"
                      color="faded"
                      class="q-pl-md"
                    />
                  </div>
                </q-item-tile>
                <q-item-tile
                  class="customer-card__bank-details-label"
                  sublabel
                >
                  {{ bankDetails.label }}
                </q-item-tile>
              </q-item-main>
            </q-item>

            <q-item-separator/>
          </template>
        </q-list>
      </div>
    </q-scroll-area>
  </q-tab-pane>
</template>

<script>
  import { businessPartnerTypesSelectOptions } from 'src/utils/businessPartnerTypes'
  import { mapGetters } from 'vuex'

  export default {
    props: {
      customer: {
        type: Object,
        required: true
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),

      formattedBusinessPartnerType () {
        return businessPartnerTypesSelectOptions
          .find((option) => option.value === this.customer.business_partner_type)
          .label
      },

      personCompany () {
        return this.customer.company ? this.customer.company.data : null
      },

      website () {
        if (this.websiteUrl) {
          return new URL(this.websiteUrl).host
        }
      },

      websiteUrl () {
        if (this.customer.website && !(this.customer.website.startsWith('http://') || this.customer.website.startsWith('https://'))) {
          return 'http://' + this.customer.website
        } else {
          return this.customer.website
        }
      }
    }
  }
</script>
